// Hideo - light formatting in the long text fields (MentionField.vue: video notes, hook content, creator bio, notes,
// strengths and weaknesses, analyses, source and saved-video notes, format cards, ideas). The text is still stored as
// plain text with "@[type:id]" mentions and \n line breaks (src/features/mentions.ts), with a few Markdown marks added,
// so old texts need no change and read the same:
//   # Heading 1 · ## Heading 2 · ### Heading 3            (at the start of a line)
//   - item  (or "* item")  · 1. item  · - [ ] to do · - [x] done   (one line each; consecutive lines make one list)
//   **bold** · *italic* · __underline__ · ~~strikethrough~~       (inside a line; the marks touch the words)
// richHtml draws the text (cards and the field itself); domToRich reads the field back; richPlain gives the words
// without the marks (search, one-line previews). Lists are not nested.
import { esc } from '../utils/html'
import type { ProfileData } from '../data/types'
import { useDataStore } from '../stores/data'
import { MENTION_RE, chipHtml, mentionPlain, validRef } from './mentions'

const HEAD_RE = /^(#{1,3}) (.+)$/
const TASK_RE = /^[-*] \[( |x|X)\] (.*)$/
const UL_RE = /^[-*•] (.*)$/
const OL_RE = /^(\d{1,4})[.)] (.*)$/

type Line =
  | { k: 'h'; level: number; text: string }
  | { k: 'ul' | 'task' | 'ol'; text: string; done?: boolean; n?: number }
  | { k: 'p'; text: string }

function parseLine(l: string): Line {
  let m = HEAD_RE.exec(l)
  if (m) return { k: 'h', level: m[1].length, text: m[2] }
  if ((m = TASK_RE.exec(l))) return { k: 'task', text: m[2], done: m[1] !== ' ' }
  if ((m = UL_RE.exec(l))) return { k: 'ul', text: m[1] }
  if ((m = OL_RE.exec(l))) return { k: 'ol', text: m[2], n: +m[1] }
  return { k: 'p', text: l }
}

/** Bold, italic, underline and strikethrough of one escaped line (the marks must touch the words: "5 * 3 * 2" stays) */
function inlineMarks(s: string): string {
  return s
    .replace(/\*\*(?=\S)(.+?)(?<=\S)\*\*/g, '<b>$1</b>')
    .replace(/__(?=\S)(.+?)(?<=\S)__/g, '<u>$1</u>')
    .replace(/~~(?=\S)(.+?)(?<=\S)~~/g, '<s>$1</s>')
    .replace(/(^|[^*\w])\*(?=[^\s*])(.+?)(?<=[^\s*])\*(?!\*)/g, '$1<i>$2</i>')
}
/** One line: escaped, with its marks and its mention chips */
function inlineHtml(raw: string, clickable: boolean, data: () => ProfileData): string {
  const chips: string[] = []
  // mentions become placeholders first, so a mark can wrap a mention and the chip's HTML is never touched
  const s = raw.replace(MENTION_RE, (_m, type: string, id: string) => { chips.push(chipHtml(type, id, clickable, data())); return `\u0001${chips.length - 1}\u0002` })
  return inlineMarks(esc(s)).replace(/\u0001(\d+)\u0002/g, (_m, i: string) => chips[+i])
}

/** Stored text → HTML: headings and lists as blocks, the other lines joined by <br> (shown the same in the field and on
 *  cards). clickable: the mention chips open their item (cards; not inside the field) */
export function richHtml(text: unknown, clickable = true): string {
  const lines = String(text || '').split('\n').map(parseLine)
  let d: ProfileData | undefined
  const data = () => (d ||= useDataStore().getData())
  const inl = (s: string) => inlineHtml(s, clickable, data)
  let out = '', run: string[] = []
  // lines before a block end with a <br> (the browser draws no empty line for it; domToRich reads it as the line's end)
  const flush = (beforeBlock: boolean) => { if (run.length) { out += run.join('<br>') + (beforeBlock ? '<br>' : ''); run = [] } }
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i]
    if (l.k === 'p') { run.push(inl(l.text)); continue }
    flush(true)
    if (l.k === 'h') { out += `<h${l.level}>${inl(l.text)}</h${l.level}>`; continue }
    // a list: this line and the next ones of the same kind
    const kind = l.k, items: Line[] = [l]
    while (i + 1 < lines.length && lines[i + 1].k === kind) items.push(lines[++i])
    const li = items.map(x => `<li${kind === 'task' && 'done' in x && x.done ? ' data-done=""' : ''}>${inl(x.text) || '<br>'}</li>`).join('')
    const first = 'n' in l && l.n && l.n !== 1 ? ` start="${l.n}"` : ''
    out += kind === 'ol' ? `<ol${first}>${li}</ol>` : kind === 'task' ? `<ul class="mf-tasks">${li}</ul>` : `<ul>${li}</ul>`
  }
  flush(false)
  return out
}

/** The words without the marks (search, one-line previews), mentions as "@name" */
export function richPlain(text: unknown, d?: ProfileData): string {
  const s = String(text || '').split('\n').map(l => {
    const p = parseLine(l)
    return p.k === 'task' ? (p.done ? '✓ ' : '☐ ') + p.text : p.k === 'ul' ? '• ' + p.text : p.k === 'ol' ? `${p.n}. ${p.text}` : p.text
  }).join('\n')
    .replace(/\*\*(?=\S)(.+?)(?<=\S)\*\*/g, '$1').replace(/__(?=\S)(.+?)(?<=\S)__/g, '$1').replace(/~~(?=\S)(.+?)(?<=\S)~~/g, '$1')
    .replace(/(^|[^*\w])\*(?=[^\s*])(.+?)(?<=[^\s*])\*(?!\*)/g, '$1$2')
  return mentionPlain(s, d)
}

const BLOCK = /^(H1|H2|H3|H4|H5|H6|UL|OL)$/
const MARKS: Record<string, string> = { B: '**', STRONG: '**', I: '*', EM: '*', U: '__', S: '~~', STRIKE: '~~', DEL: '~~' }
/** The mark of an inline element ('' for none; a span styled by the browser counts too) */
function markOf(e: HTMLElement): string {
  if (MARKS[e.tagName]) return MARKS[e.tagName]
  if (e.tagName !== 'SPAN') return ''
  if (/bold|[6-9]00/.test(e.style.fontWeight)) return '**'
  if (e.style.fontStyle === 'italic') return '*'
  return /line-through/.test(e.style.textDecoration) ? '~~' : /underline/.test(e.style.textDecoration) ? '__' : ''
}
/** inner between the marks, which touch the words: spaces stay outside, empty text gets none */
function wrap(mark: string, inner: string): string {
  if (!mark) return inner
  // the marks never cross a line break: each line gets its own
  return inner.split('\n').map(seg => {
    if (!seg.trim()) return seg
    const m = /^(\s*)([\s\S]*?)(\s*)$/.exec(seg) as RegExpExecArray
    return `${m[1]}${mark}${m[2]}${mark}${m[3]}`
  }).join('\n')
}
/** Inline content of an element → text with marks and references (a line break becomes \n; in a list item a space) */
function inlineText(el: Node, inItem = false): string {
  let out = ''
  el.childNodes.forEach(c => {
    if (c.nodeType === 3) { out += c.textContent; return }
    if (c.nodeType !== 1) return
    const e = c as HTMLElement
    if (e.dataset && validRef(e.dataset.ref)) { out += `@[${e.dataset.ref}]`; return }
    if (e.tagName === 'BR') { out += inItem ? ' ' : '\n'; return }
    out += wrap(markOf(e), inlineText(e, inItem))
  })
  return out
}

/** Field content → stored text (the reverse of richHtml; plain lines are read as mfGet reads them: Chrome / Safari
 *  lines arrive as <div> / <br>) */
export function domToRich(root: HTMLElement): string {
  let out = ''
  const walk = (n: Node) => n.childNodes.forEach(c => {
    if (c.nodeType === 3) { out += c.textContent; return }
    if (c.nodeType !== 1) return
    const e = c as HTMLElement
    if (e.dataset && validRef(e.dataset.ref)) { out += `@[${e.dataset.ref}]`; return }
    if (e.tagName === 'BR') { out += '\n'; return }
    if (BLOCK.test(e.tagName)) {
      // a block starts on its own line
      if (out && !out.endsWith('\n')) out += '\n'
      if (/^H\d$/.test(e.tagName)) {
        const txt = inlineText(e, true).trim()
        out += txt ? `${'#'.repeat(Math.min(3, +e.tagName[1]))} ${txt}\n` : '\n'
        return
      }
      // a list: one line per item; nested lists are flattened (each item once, in the outer list's kind)
      const tasks = e.tagName === 'UL' && e.classList.contains('mf-tasks')
      let n = e.tagName === 'OL' ? Number(e.getAttribute('start')) || 1 : 0
      e.querySelectorAll('li').forEach(li => {
        const own = li.cloneNode(true) as HTMLElement
        own.querySelectorAll('ul, ol').forEach(x => x.remove())
        const pre = e.tagName === 'OL' ? `${n++}. ` : tasks ? `- [${li.hasAttribute('data-done') ? 'x' : ' '}] ` : '- '
        out += pre + inlineText(own, true).trim() + '\n'
      })
      return
    }
    if (/^(DIV|P)$/.test(e.tagName)) { if (out && !out.endsWith('\n')) out += '\n'; walk(e); return }
    const mark = markOf(e)
    if (mark) { out += wrap(mark, inlineText(e)); return }
    walk(e)
  })
  walk(root)
  return out.replace(/ /g, ' ').replace(/\n+$/, '')
}
